import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ArrowRightIcon } from 'lucide-react';
type Specialty = 'cardiologist' | 'nephrologist' | 'gp' | 'endocrinologist';
interface DriversBarriersProps {
  segment: string;
  selectedSpecialty: Specialty;
  onContinue: () => void;
}
const drivers = ['Robust long-term clinical trial data', 'Clear cardiovascular and renal outcome benefits', 'Strong safety profile with manageable side effects', 'Alignment with clinical guidelines', 'Positive peer experiences and recommendations'];
const barriers = ['Limited long-term safety data', 'Uncertainty in real-world effectiveness', 'Complex patient insurance coverage', 'Lack of head-to-head comparison studies', 'Concerns about patient adherence'];
export function DriversBarriers({
  segment,
  selectedSpecialty,
  onContinue
}: DriversBarriersProps) {
  return <div className="w-full min-h-screen flex items-start justify-center bg-[#FAFAF8] px-6 py-16 overflow-y-auto" data-id="element-45">
      <div className="max-w-xl w-full space-y-6" data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-2" data-id="element-47">
          <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full uppercase tracking-wide" data-id="element-48">
            Personalized
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917]" data-id="element-49">
            What drives and blocks you
          </h2>
          <p className="text-sm text-[#57534E]" data-id="element-50">
            Based on your Evidence Optimizer profile
          </p>
        </motion.div>

        {/* Drivers */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 space-y-4" data-id="element-51">
          <div className="flex items-center gap-2" data-id="element-52">
            <CheckCircleIcon className="w-5 h-5 text-emerald-500" data-id="element-53" />
            <h3 className="text-base font-bold text-[#1C1917]" data-id="element-54">Drivers</h3>
          </div>
          <div className="space-y-2" data-id="element-55">
            {drivers.map((driver, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.15 + 0.05 * i
          }} className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50/50" data-id="element-56">
                <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-57" />
                <span className="text-sm text-[#1C1917] font-medium" data-id="element-58">
                  {driver}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Barriers */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 space-y-4" data-id="element-59">
          <div className="flex items-center gap-2" data-id="element-60">
            <XCircleIcon className="w-5 h-5 text-rose-500" data-id="element-61" />
            <h3 className="text-base font-bold text-[#1C1917]" data-id="element-62">Barriers</h3>
          </div>
          <div className="space-y-2" data-id="element-63">
            {barriers.map((barrier, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.25 + 0.05 * i
          }} className="flex items-start gap-3 p-3 rounded-xl bg-rose-50/50" data-id="element-64">
                <XCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-65" />
                <span className="text-sm text-[#1C1917] font-medium" data-id="element-66">
                  {barrier}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="flex justify-center pt-4 pb-20" data-id="element-67">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-68">
            Continue
            <ArrowRightIcon className="w-5 h-5" data-id="element-69" />
          </button>
        </motion.div>
      </div>
    </div>;
}